import { useState, useEffect, useRef, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import { PageHeader, Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import {
  Upload, Send, RefreshCw, ChevronRight, CheckCircle2, XCircle,
  Clock, ArrowLeft, Loader2, MessageSquare, Plus, FileSpreadsheet,
  Phone, AlertCircle, RotateCcw, ChevronDown, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { WaTemplates } from "./WaTemplates";
import { WaAnalytics } from "./WaAnalytics";

// ─── helpers ─────────────────────────────────────────────────────────────────
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
}

function resolvePreview(bodyText, bodyParamsColumns, row) {
  if (!bodyText || !row) return bodyText || "";
  return bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const colIdx = bodyParamsColumns[parseInt(n, 10) - 1];
    if (colIdx === undefined || colIdx === null || colIdx === "") return `{{${n}}}`;
    return row[colIdx] || "";
  });
}

const STATUS_CFG = {
  completed:  { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="w-3 h-3" />, label: "Completed" },
  running:    { cls: "bg-blue-100 text-blue-700",       icon: <Loader2 className="w-3 h-3 animate-spin" />, label: "Sending…" },
  draft:      { cls: "bg-slate-100 text-slate-600",     icon: <Clock className="w-3 h-3" />,        label: "Draft" },
  failed:     { cls: "bg-rose-100 text-rose-700",       icon: <XCircle className="w-3 h-3" />,      label: "Failed" },
};

function StatusBadge({ status }) {
  const s = STATUS_CFG[status] || STATUS_CFG.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon}{s.label}
    </span>
  );
}

const RECIP_STATUS = {
  sent:    "bg-emerald-100 text-emerald-700",
  failed:  "bg-rose-100 text-rose-700",
  skipped: "bg-slate-100 text-slate-500",
  pending: "bg-amber-100 text-amber-700",
};

// ─── Campaign History ────────────────────────────────────────────────────────
function HistoryView({ campaigns, onNew, onOpen, loading }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Bulk WhatsApp Campaigns</h2>
          <p className="text-sm text-slate-500 mt-0.5">Send a WhatsApp template to any list of contacts from an Excel/CSV sheet.</p>
        </div>
        <button className={`${btnPrimary} flex items-center gap-2`} onClick={onNew} data-testid="new-campaign-btn">
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {loading ? <Loading /> : campaigns.length === 0 ? (
        <Card className="py-16 flex flex-col items-center gap-3 text-slate-400">
          <MessageSquare className="w-10 h-10" />
          <p className="text-sm">No campaigns yet. Click <strong>New Campaign</strong> to get started.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <Card
              key={c.id}
              className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => onOpen(c)}
              data-testid="campaign-row"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 text-sm">{c.name}</span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Template: <span className="font-mono">{c.template_name}</span> · Created {fmtTime(c.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs shrink-0">
                <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />{c.sent_count} sent</span>
                <span className="flex items-center gap-1 text-rose-600"><XCircle className="w-3.5 h-3.5" />{c.failed_count} failed</span>
                <span className="text-slate-400">{c.total_count} total</span>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Campaign Detail ─────────────────────────────────────────────────────────
function CampaignDetail({ campaign: initCampaign, onBack, onRefresh }) {
  const [campaign, setCampaign] = useState(initCampaign);
  const [recipients, setRecipients] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/bulk/campaigns/${campaign.id}`);
      setCampaign(data);
      if (data.status !== "running") clearInterval(pollRef.current);
    } catch { /* silent */ }
  }, [campaign.id]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (campaign.status === "running") {
      pollRef.current = setInterval(refresh, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [campaign.status, refresh]);

  const loadRecipients = useCallback(async () => {
    setRecLoading(true);
    try {
      const { data } = await api.get(`/bulk/campaigns/${campaign.id}/recipients?limit=200`);
      setRecipients(data.items || []);
    } catch { setRecipients([]); }
    setRecLoading(false);
  }, [campaign.id]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const retryFailed = async () => {
    setRetrying(true);
    try {
      await api.post(`/bulk/campaigns/${campaign.id}/retry-failed`);
      toast.success("Failed recipients re-queued. Sending…");
      await refresh();
      await loadRecipients();
      if (!pollRef.current) {
        pollRef.current = setInterval(refresh, 3000);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setRetrying(false);
  };

  const pct = campaign.total_count > 0
    ? Math.round(((campaign.sent_count + campaign.failed_count + campaign.skipped_count) / campaign.total_count) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className={`${btnSecondary} flex items-center gap-1.5 text-sm`} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-slate-900">{campaign.name}</h2>
            <StatusBadge status={campaign.status} />
          </div>
          <div className="text-xs text-slate-500">Template: <span className="font-mono">{campaign.template_name}</span> · {fmtTime(campaign.created_at)}</div>
        </div>
        <div className="flex gap-2">
          {campaign.failed_count > 0 && campaign.status !== "running" && (
            <button onClick={retryFailed} disabled={retrying} className={`${btnSecondary} flex items-center gap-1.5 text-sm`} data-testid="retry-failed-btn">
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Retry Failed ({campaign.failed_count})
            </button>
          )}
          <button onClick={() => { refresh(); loadRecipients(); }} className={`${btnSecondary} px-2.5`} data-testid="refresh-campaign-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        {[
          { label: "Total", val: campaign.total_count, cls: "text-slate-700" },
          { label: "Sent", val: campaign.sent_count, cls: "text-emerald-600" },
          { label: "Delivered", val: campaign.tracking?.delivered ?? 0, cls: "text-sky-600" },
          { label: "Read", val: campaign.tracking?.read ?? 0, cls: "text-emerald-700" },
          { label: "Clicked", val: campaign.tracking?.clicked ?? 0, cls: "text-violet-600" },
          { label: "Failed", val: Math.max(campaign.failed_count, campaign.tracking?.failed ?? 0), cls: "text-rose-600" },
          { label: "Skipped", val: campaign.skipped_count, cls: "text-slate-400" },
        ].map((s) => (
          <Card key={s.label} className="p-4 text-center">
            <div className={`text-2xl font-bold ${s.cls}`}>{s.val}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Progress bar */}
      {campaign.status === "running" && (
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /> Sending…</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </Card>
      )}

      {/* Recipients table */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700 flex items-center justify-between">
          <span>Recipients</span>
          {recLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
        {recLoading && recipients.length === 0 ? (
          <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : recipients.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">No recipients found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">#</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Phone</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Delivery</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Clicked</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Detail</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Sent At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recipients.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid="recipient-row">
                    <td className="px-4 py-2.5 text-slate-400">{r.row_index + 1}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-700">{r.phone || r.phone_raw || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RECIP_STATUS[r.status] || RECIP_STATUS.pending}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 capitalize" data-testid="recipient-delivery">{r.delivery_status && r.delivery_status !== "sent" ? r.delivery_status : "—"}</td>
                    <td className="px-4 py-2.5 text-violet-700" data-testid="recipient-clicked">{r.click_count ? `${r.last_button || "Yes"}${r.click_count > 1 ? ` (${r.click_count})` : ""}` : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-xs truncate">{r.delivery_error || r.failure_reason || r.wamid || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-400">{r.sent_at ? fmtTime(r.sent_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── New Campaign Wizard ──────────────────────────────────────────────────────
const STEP_LABELS = ["Upload Sheet", "Configure", "Review", "Sending"];

function WizardProgress({ step }) {
  return (
    <div className="flex items-center gap-0 mb-6">
      {STEP_LABELS.map((label, i) => (
        <div key={i} className="flex items-center flex-1 last:flex-none">
          <div className={`flex items-center gap-2 ${i <= step ? "text-blue-600" : "text-slate-400"}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < step ? "bg-blue-600 text-white" : i === step ? "border-2 border-blue-600 text-blue-600" : "border-2 border-slate-200 text-slate-400"}`}>
              {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className="text-xs font-medium hidden sm:block">{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function WizardView({ onBack, onDone }) {
  const [step, setStep] = useState(0);
  const [sheet, setSheet] = useState(null);        // { headers, rows, total }
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const fileRef = useRef(null);

  const [cfg, setCfg] = useState({
    name: "",
    template_name: "",
    language_code: "en",
    phone_column: 0,
    body_params_columns: [],
    header_column: null,
    button_column: null,
  });

  const [creating, setCreating] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const selectedTpl = templates.find((t) => t.name === cfg.template_name);
  const bodyCount = selectedTpl?.body_param_count || 0;

  const loadTemplates = async () => {
    setTplLoading(true);
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates((data.templates || []).filter((t) => t.status === "APPROVED" || !t.status));
    } catch { toast.error("Could not load WhatsApp templates"); }
    setTplLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  // ── Step 0: Upload ──────────────────────────────────────────────────────────
  const handleFileDrop = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/bulk/upload-sheet", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSheet(data);
      // Auto-set phone column to first column containing "phone"/"mobile" in header
      const phoneIdx = data.headers.findIndex((h) =>
        /phone|mobile|mob|whatsapp/i.test(h)
      );
      setCfg((p) => ({ ...p, phone_column: phoneIdx >= 0 ? phoneIdx : 0 }));
      setStep(1);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Failed to parse file");
    }
    setUploading(false);
  };

  // ── Step 1: Configure ───────────────────────────────────────────────────────
  const handleTplChange = (name) => {
    const tpl = templates.find((t) => t.name === name);
    const count = tpl?.body_param_count || 0;
    setCfg((p) => ({
      ...p,
      template_name: name,
      language_code: tpl?.language || "en",
      body_params_columns: Array(count).fill(null),
      header_column: tpl?.header_has_var ? null : null,
      button_column: null, button_columns: [], button_values: [],
    }));
  };

  const setBodyCol = (i, val) => {
    setCfg((p) => {
      const cols = [...(p.body_params_columns || [])];
      cols[i] = val === "" ? null : Number(val);
      return { ...p, body_params_columns: cols };
    });
  };

  const canConfigure = cfg.template_name && cfg.phone_column !== null &&
    (bodyCount === 0 || cfg.body_params_columns.every((c) => c !== null && c !== "")) &&
    (selectedTpl?.url_buttons || []).every((_, k) => (cfg.button_columns || [])[k] != null || !!((cfg.button_values || [])[k] || "").trim());

  // ── Step 2: Review ──────────────────────────────────────────────────────────
  const reviewRows = sheet?.rows || [];
  const validCount = reviewRows.filter((r) => {
    const phone = r[cfg.phone_column] || "";
    return /^[+]?[\d\s-]{10,15}$/.test(phone.replace(/\s/g, ""));
  }).length;
  const invalidCount = reviewRows.length - validCount;

  // ── Step 3: Create + Start ──────────────────────────────────────────────────
  const startCampaign = async () => {
    setCreating(true);
    try {
      const { data: created } = await api.post("/bulk/campaigns", {
        name: cfg.name || `Campaign ${new Date().toLocaleDateString("en-IN")}`,
        template_name: cfg.template_name,
        language_code: cfg.language_code,
        phone_column: cfg.phone_column,
        body_params_columns: cfg.body_params_columns.map(Number),
        header_column: cfg.header_column !== null ? Number(cfg.header_column) : null,
        button_columns: (selectedTpl?.url_buttons || []).map((_, k) => (cfg.button_columns || [])[k] ?? null),
        button_values: (selectedTpl?.url_buttons || []).map((_, k) => ((cfg.button_values || [])[k] || "").trim()),
        headers: sheet.headers,
        rows: sheet.rows,
      });
      await api.post(`/bulk/campaigns/${created.id}/start`);
      const { data: full } = await api.get(`/bulk/campaigns/${created.id}`);
      setCampaign(full);
      setStep(3);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Failed to start campaign");
    }
    setCreating(false);
  };

  const colOptions = (sheet?.headers || []).map((h, i) => ({ label: `${i + 1}. ${h}`, value: i }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={step > 0 && step < 3 ? () => setStep((s) => s - 1) : onBack}
          className={`${btnSecondary} flex items-center gap-1.5 text-sm`}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {step === 0 || step === 3 ? "Back to Campaigns" : "Back"}
        </button>
        <h2 className="text-lg font-semibold text-slate-900">New Bulk Campaign</h2>
      </div>

      <WizardProgress step={step} />

      {/* ── Step 0: Upload ── */}
      {step === 0 && (
        <Card className="p-8 space-y-6">
          <div className="text-center space-y-2">
            <FileSpreadsheet className="w-12 h-12 text-blue-400 mx-auto" />
            <h3 className="font-semibold text-slate-800">Upload Your Contact Sheet</h3>
            <p className="text-sm text-slate-500">Accepts <strong>.xlsx</strong> or <strong>.csv</strong> files. First row must be column headers. Max 1,000 rows.</p>
          </div>
          <div
            className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/40 transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFileDrop(e.dataTransfer.files[0]); }}
            data-testid="upload-dropzone"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <Loader2 className="w-5 h-5 animate-spin" /> Parsing file…
              </div>
            ) : (
              <div className="space-y-2 text-slate-500">
                <Upload className="w-8 h-8 mx-auto text-slate-300" />
                <p className="text-sm">Click to browse or drag & drop your file here</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
            onChange={(e) => handleFileDrop(e.target.files[0])} data-testid="file-input" />
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">Sheet format tips:</p>
            <p>• Row 1: Column headers (e.g. Name, Mobile, Amount)</p>
            <p>• One mobile number column (10-digit Indian numbers work automatically)</p>
            <p>• One row per recipient</p>
          </div>
        </Card>
      )}

      {/* ── Step 1: Configure ── */}
      {step === 1 && sheet && (
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <h3 className="font-semibold text-slate-800">Sheet Preview</h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>{sheet.headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold text-slate-600 border-r border-slate-200 last:border-0">{h || `Col ${i + 1}`}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(sheet.rows || []).slice(0, 3).map((row, ri) => (
                    <tr key={ri}>{row.map((cell, ci) => <td key={ci} className="px-3 py-2 text-slate-600 border-r border-slate-200 last:border-0 truncate max-w-[120px]">{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400">{sheet.total} rows detected · showing first 3</p>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="font-semibold text-slate-800">Campaign Settings</h3>
            <div>
              <label className={labelCls}>Campaign Name (optional)</label>
              <input className={inputCls} placeholder="e.g. Drone Workshop Reminder Oct 2026"
                value={cfg.name} onChange={(e) => setCfg((p) => ({ ...p, name: e.target.value }))}
                data-testid="campaign-name-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>WhatsApp Template *</label>
                <div className="flex gap-2">
                  <select className={inputCls} value={cfg.template_name}
                    onChange={(e) => handleTplChange(e.target.value)} data-testid="template-select">
                    <option value="">— select approved template —</option>
                    {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                  <button onClick={loadTemplates} disabled={tplLoading} className={`${btnSecondary} px-2.5`} title="Refresh" data-testid="refresh-templates-btn">
                    {tplLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </button>
                </div>
                {cfg.language_code && cfg.template_name && (
                  <p className="text-xs text-slate-400 mt-1">Language: {cfg.language_code}</p>
                )}
              </div>

              <div>
                <label className={labelCls}>Phone Number Column *</label>
                <select className={inputCls} value={cfg.phone_column}
                  onChange={(e) => setCfg((p) => ({ ...p, phone_column: Number(e.target.value) }))}
                  data-testid="phone-column-select">
                  {colOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">10-digit Indian numbers are auto-normalized</p>
              </div>
            </div>

            {selectedTpl && selectedTpl.body_text && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-600">Template Message</div>
                <pre className="text-xs text-slate-700 font-mono whitespace-pre-wrap bg-white rounded-lg p-3 border border-slate-200">{selectedTpl.body_text}</pre>

                {selectedTpl.header_has_var && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Header Variable → Column</label>
                    <select className={inputCls} value={cfg.header_column ?? ""}
                      onChange={(e) => setCfg((p) => ({ ...p, header_column: e.target.value === "" ? null : Number(e.target.value) }))}
                      data-testid="header-column-select">
                      <option value="">— select column —</option>
                      {colOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                )}

                {bodyCount > 0 && (
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-600">
                      Body Variable Mapping ({`{{1}}`} to {`{{${bodyCount}}}`})
                    </label>
                    {Array.from({ length: bodyCount }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs font-mono text-slate-500 w-8 shrink-0">{`{{${i + 1}}}`}</span>
                        <select className={`${inputCls} flex-1`}
                          value={cfg.body_params_columns[i] ?? ""}
                          onChange={(e) => setBodyCol(i, e.target.value)}
                          data-testid={`body-param-col-${i}`}>
                          <option value="">— select column —</option>
                          {colOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {(selectedTpl.url_buttons || []).map((b, k) => {
                  const col = (cfg.button_columns || [])[k];
                  const val = (cfg.button_values || [])[k] || "";
                  const setArr = (key, v) => setCfg((p) => { const a = [...(p[key] || [])]; a[k] = v; return { ...p, [key]: a }; });
                  return (
                    <div key={b.index} data-testid={`url-button-map-${k}`}>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Button "{b.text}" → {b.tracked ? "destination link (clicks tracked)" : `value for {{1}} in ${b.url}`}
                      </label>
                      <select className={inputCls} value={col ?? ""} onChange={(e) => setArr("button_columns", e.target.value === "" ? null : Number(e.target.value))} data-testid={`button-column-select-${k}`}>
                        <option value="">— same link for everyone (type below) —</option>
                        {colOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {col == null && (
                        <>
                          <input className={`${inputCls} mt-2`} placeholder={b.tracked ? "e.g. https://pay.gdiot.in/r/your-page" : "Value for {{1}}"} value={val}
                            onChange={(e) => setArr("button_values", e.target.value)} data-testid={`button-static-value-input-${k}`} />
                          {!val.trim() && <p className="text-[11px] text-rose-600 mt-1">Required — without it Meta rejects every message.</p>}
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Live preview using row 0 */}
                {sheet.rows.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-slate-600 mb-1">Preview (Row 1)</div>
                    <div className="bg-[#dcf8c6] rounded-xl rounded-tl-sm px-4 py-2.5 text-sm text-slate-800 font-sans max-w-xs leading-relaxed whitespace-pre-wrap">
                      {resolvePreview(selectedTpl.body_text, cfg.body_params_columns, sheet.rows[0])}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pt-2 flex justify-end">
              <button
                className={btnPrimary}
                onClick={() => setStep(2)}
                disabled={!canConfigure}
                data-testid="next-to-review-btn"
              >
                Review Contacts <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* ── Step 2: Review ── */}
      {step === 2 && sheet && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Total Contacts", val: reviewRows.length, cls: "text-slate-700" },
              { label: "Valid Numbers", val: validCount, cls: "text-emerald-600" },
              { label: "Will be Skipped", val: invalidCount, cls: "text-rose-500" },
            ].map((s) => (
              <Card key={s.label} className="p-4 text-center">
                <div className={`text-2xl font-bold ${s.cls}`}>{s.val}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </Card>
            ))}
          </div>

          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">
              Contact List
            </div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500">#</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500">
                      Phone ({sheet.headers[cfg.phone_column] || `Col ${cfg.phone_column + 1}`})
                    </th>
                    {cfg.body_params_columns.map((colIdx, i) => (
                      <th key={i} className="px-3 py-2 text-left font-semibold text-slate-500">
                        {`{{${i + 1}}}`} ({sheet.headers[colIdx] || `Col ${colIdx + 1}`})
                      </th>
                    ))}
                    <th className="px-3 py-2 text-left font-semibold text-slate-500">Valid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {reviewRows.map((row, ri) => {
                    const phone = row[cfg.phone_column] || "";
                    const isValid = /^[+]?[\d\s-]{10,15}$/.test(phone.replace(/\s/g, ""));
                    return (
                      <tr key={ri} className={!isValid ? "bg-rose-50" : "hover:bg-slate-50"} data-testid="review-row">
                        <td className="px-3 py-2 text-slate-400">{ri + 1}</td>
                        <td className="px-3 py-2 font-mono">{phone || "—"}</td>
                        {cfg.body_params_columns.map((colIdx, ci) => (
                          <td key={ci} className="px-3 py-2 text-slate-600 max-w-[100px] truncate">{row[colIdx] || "—"}</td>
                        ))}
                        <td className="px-3 py-2">
                          {isValid ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-rose-400" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {validCount} message{validCount !== 1 ? "s" : ""} will be sent
              {invalidCount > 0 && <span className="text-rose-500"> · {invalidCount} skipped (invalid phone)</span>}
            </p>
            <button
              className={`${btnPrimary} flex items-center gap-2`}
              onClick={startCampaign}
              disabled={creating || validCount === 0}
              data-testid="send-campaign-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {creating ? "Starting…" : `Send to ${validCount} Contacts`}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Sending / Results ── */}
      {step === 3 && campaign && (
        <CampaignDetail
          campaign={campaign}
          onBack={onDone}
          onRefresh={() => {}}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BulkWhatsApp() {
  const [view, setView] = useState("history"); // "history" | "wizard" | "detail"
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [tab, setTab] = useState("campaigns");

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bulk/campaigns");
      setCampaigns(data || []);
    } catch { setCampaigns([]); }
    setLoading(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title="WhatsApp Bulk"
        subtitle="Send WhatsApp template messages to any list of contacts from an Excel or CSV sheet."
      />

      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit" data-testid="bulk-tabs">
        {[["campaigns", "Campaigns"], ["templates", "Templates"], ["analytics", "Analytics & Clicks"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bulk-tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "templates" && <WaTemplates />}
      {tab === "analytics" && <WaAnalytics />}

      {tab === "campaigns" && view === "history" && (
        <HistoryView
          campaigns={campaigns}
          loading={loading}
          onNew={() => setView("wizard")}
          onOpen={(c) => { setSelectedCampaign(c); setView("detail"); }}
        />
      )}

      {tab === "campaigns" && view === "wizard" && (
        <WizardView
          onBack={() => { setView("history"); loadCampaigns(); }}
          onDone={() => { setView("history"); loadCampaigns(); }}
        />
      )}

      {tab === "campaigns" && view === "detail" && selectedCampaign && (
        <CampaignDetail
          campaign={selectedCampaign}
          onBack={() => { setView("history"); loadCampaigns(); setSelectedCampaign(null); }}
          onRefresh={loadCampaigns}
        />
      )}
    </div>
  );
}
